/**
 * Postedición automática de fotos (spec 011-postedicion-fotos, US2):
 * qué foto enseñar y estado del conmutador con guardado optimista.
 */

import { useEffect, useState } from 'react';
import { toast } from 'sonner';

export interface EnhancementUrls {
  /** URL que devuelve la API: la que usa hoy el vídeo */
  url: string;
  /** Recortada, sin editar */
  processedUrl?: string | null;
  /** Editada; vacía si aún no existe */
  enhancedUrl?: string | null;
}

/** Qué foto enseñar según el estado del conmutador y si se está comparando */
export function photoToShow({ url, processedUrl, enhancedUrl }: EnhancementUrls, enabled: boolean, showOriginal: boolean): string {
  if (!enhancedUrl) return url;
  if (showOriginal || !enabled) return processedUrl || url;
  return enhancedUrl;
}

/**
 * Estado del conmutador con guardado optimista: si la API falla, vuelve atrás
 * y avisa.
 */
export function usePhotoEnhancement(initialEnabled: boolean | undefined, save: (enabled: boolean) => Promise<unknown>) {
  const [enabled, setEnabled] = useState(initialEnabled ?? true);
  const [saving, setSaving] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  useEffect(() => {
    setEnabled(initialEnabled ?? true);
  }, [initialEnabled]);

  const toggle = async (next: boolean) => {
    const previous = enabled;
    setEnabled(next);
    setSaving(true);
    try {
      await save(next);
    } catch {
      setEnabled(previous);
      toast.error('No se ha podido guardar. Inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  return { enabled, saving, showOriginal, setShowOriginal, toggle };
}
